export type EvaluationPlanAttachmentKind = 'placeholder' | 'file' | 'form' | 'share_link' | 'web_document';

/** 평가 기준안·평가지 첨부 메타 (파일 업로드·양식 입력·공유링크 확장용) */
export type EvaluationPlanAttachment = {
  kind: EvaluationPlanAttachmentKind;
  registered_at: string;
  slot?: EvaluationPlanAttachmentSlot;
  file_name?: string;
  mime_type?: string;
  storage_path?: string;
  share_url?: string;
  form_data?: Record<string, unknown>;
  preview_label?: string;
};

export type EvaluationPlanAttachmentSlot = 'criterion' | 'sheet';

export const EVALUATION_PLAN_ATTACHMENT_SLOT_LABELS: Record<EvaluationPlanAttachmentSlot, string> = {
  criterion: '평가 기준안',
  sheet: '평가지',
};

export function parseEvaluationPlanAttachment(raw: unknown): EvaluationPlanAttachment | null {
  if (!raw || typeof raw !== 'object') return null;
  const obj = raw as Partial<EvaluationPlanAttachment>;
  if (typeof obj.registered_at !== 'string' || !obj.registered_at.trim()) return null;
  const kind = obj.kind;
  if (kind !== 'placeholder' && kind !== 'file' && kind !== 'form' && kind !== 'share_link' && kind !== 'web_document') {
    return null;
  }
  return {
    kind,
    registered_at: obj.registered_at,
    file_name: typeof obj.file_name === 'string' ? obj.file_name : undefined,
    mime_type: typeof obj.mime_type === 'string' ? obj.mime_type : undefined,
    storage_path: typeof obj.storage_path === 'string' ? obj.storage_path : undefined,
    share_url: typeof obj.share_url === 'string' ? obj.share_url : undefined,
    slot: obj.slot === 'criterion' || obj.slot === 'sheet' ? obj.slot : undefined,
    form_data:
      obj.form_data && typeof obj.form_data === 'object' && !Array.isArray(obj.form_data)
        ? (obj.form_data as Record<string, unknown>)
        : undefined,
    preview_label: typeof obj.preview_label === 'string' ? obj.preview_label : undefined,
  };
}

export function hasEvaluationPlanAttachment(
  attachment: EvaluationPlanAttachment | null | undefined
): boolean {
  return parseEvaluationPlanAttachment(attachment) != null;
}

export function cloneEvaluationPlanAttachment(
  attachment: EvaluationPlanAttachment | null | undefined
): EvaluationPlanAttachment | null {
  const parsed = parseEvaluationPlanAttachment(attachment);
  if (!parsed) return null;
  return {
    ...parsed,
    form_data: parsed.form_data ? { ...parsed.form_data } : undefined,
  };
}

export function cloneEvaluationPlanDraft(plan: EvaluationPlanDraft): EvaluationPlanDraft {
  return {
    ...plan,
    ...EMPTY_EVALUATION_LEVELS,
    ...plan,
    standard_ids: [...(plan.standard_ids ?? [])],
    criterion_rubric_attachment: cloneEvaluationPlanAttachment(plan.criterion_rubric_attachment),
    evaluation_sheet_attachment: cloneEvaluationPlanAttachment(plan.evaluation_sheet_attachment),
  };
}

export function hasEvaluationPlanSlotAttachment(
  plan: Pick<EvaluationPlanDraft, 'criterion_rubric_attachment' | 'evaluation_sheet_attachment'>,
  slot: EvaluationPlanAttachmentSlot
): boolean {
  const attachment = getEvaluationPlanAttachment(plan, slot);
  if (!attachment) return false;
  if (attachment.slot && attachment.slot !== slot) return false;
  return true;
}

export type EvaluationPlanAttachmentRegisterMethod = 'share_link' | 'web_document';

export function getEvaluationPlanAttachmentRegisterMethod(
  attachment: EvaluationPlanAttachment | null | undefined
): EvaluationPlanAttachmentRegisterMethod | null {
  if (!attachment) return null;
  if (attachment.kind === 'share_link') return 'share_link';
  if (attachment.kind === 'web_document') return 'web_document';
  return null;
}

export function getEvaluationPlanAttachment(
  plan: Pick<EvaluationPlanDraft, 'criterion_rubric_attachment' | 'evaluation_sheet_attachment'>,
  slot: EvaluationPlanAttachmentSlot
): EvaluationPlanAttachment | null {
  const raw =
    slot === 'criterion' ? plan.criterion_rubric_attachment : plan.evaluation_sheet_attachment;
  return parseEvaluationPlanAttachment(raw);
}

export function createPlaceholderPlanAttachment(previewLabel: string): EvaluationPlanAttachment {
  return {
    kind: 'placeholder',
    registered_at: new Date().toISOString(),
    preview_label: previewLabel.trim() || undefined,
  };
}

export function createShareLinkPlanAttachment(previewLabel: string, shareUrl: string): EvaluationPlanAttachment {
  const normalized = shareUrl.trim();
  return {
    kind: 'share_link',
    registered_at: new Date().toISOString(),
    preview_label: previewLabel.trim() || undefined,
    share_url: normalized,
    file_name: undefined,
  };
}

export type EvaluationPlanLevelFields = {
  level_a: string;
  level_b: string;
  level_c: string;
  level_d: string;
  level_e: string;
};

export type EvaluationLevelStepCount = 2 | 3 | 4 | 5;

export const EVALUATION_LEVEL_KEYS = ['level_a', 'level_b', 'level_c', 'level_d', 'level_e'] as const;
export const EVALUATION_LEVEL_LABEL_KEYS = [
  'level_label_a',
  'level_label_b',
  'level_label_c',
  'level_label_d',
  'level_label_e',
] as const;
export const EVALUATION_LEVEL_LABELS = ['A', 'B', 'C', 'D', 'E'] as const;
export const DEFAULT_LEVEL_STEP_COUNT: EvaluationLevelStepCount = 5;
export const LEVEL_STEP_COUNT_OPTIONS: EvaluationLevelStepCount[] = [2, 3, 4, 5];

export type EvaluationLevelLabelKey = (typeof EVALUATION_LEVEL_LABEL_KEYS)[number];

export type EvaluationLevelDisplayLabels = Record<EvaluationLevelLabelKey, string>;

export const EMPTY_LEVEL_DISPLAY_LABELS: EvaluationLevelDisplayLabels = {
  level_label_a: '',
  level_label_b: '',
  level_label_c: '',
  level_label_d: '',
  level_label_e: '',
};

export type EvaluationLevelKey = (typeof EVALUATION_LEVEL_KEYS)[number];

export function clampLevelStepCount(value: unknown): EvaluationLevelStepCount {
  if (value == null || value === '') return DEFAULT_LEVEL_STEP_COUNT;
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return DEFAULT_LEVEL_STEP_COUNT;
  if (n <= 2) return 2;
  if (n >= 5) return 5;
  return Math.round(n) as EvaluationLevelStepCount;
}

export function parseEvaluationLevelDisplayLabels(
  raw: Partial<EvaluationLevelDisplayLabels> | null | undefined
): EvaluationLevelDisplayLabels {
  return {
    level_label_a: typeof raw?.level_label_a === 'string' ? raw.level_label_a : '',
    level_label_b: typeof raw?.level_label_b === 'string' ? raw.level_label_b : '',
    level_label_c: typeof raw?.level_label_c === 'string' ? raw.level_label_c : '',
    level_label_d: typeof raw?.level_label_d === 'string' ? raw.level_label_d : '',
    level_label_e: typeof raw?.level_label_e === 'string' ? raw.level_label_e : '',
  };
}

export function getActiveLevelKeys(stepCount: EvaluationLevelStepCount): EvaluationLevelKey[] {
  return EVALUATION_LEVEL_KEYS.slice(0, stepCount);
}

export function getActiveLevelLabelKeys(stepCount: EvaluationLevelStepCount): EvaluationLevelLabelKey[] {
  return EVALUATION_LEVEL_LABEL_KEYS.slice(0, stepCount);
}

export function getActiveLevelLabels(
  stepCount: EvaluationLevelStepCount,
  displayLabels?: Partial<EvaluationLevelDisplayLabels> | null
): string[] {
  const parsed = parseEvaluationLevelDisplayLabels(displayLabels);
  return getActiveLevelLabelKeys(stepCount).map((labelKey, idx) => {
    const custom = parsed[labelKey].trim();
    return custom || EVALUATION_LEVEL_LABELS[idx];
  });
}

/** 성취기준·평가계획 모달 등 성취수준 블록 헤드 라벨 */
export function formatLevelHeadLabel(displayText: string): string {
  const text = displayText.trim();
  return text ? `성취수준 - ${text}` : '성취수준';
}

export function formatLevelStepOptionLabel(
  stepCount: EvaluationLevelStepCount,
  displayLabels?: Partial<EvaluationLevelDisplayLabels> | null
): string {
  return `${stepCount}단계 (${getActiveLevelLabels(stepCount, displayLabels).join(' · ')})`;
}


export function levelDisplayLabelsToDbPayload(labels: EvaluationLevelDisplayLabels): EvaluationLevelDisplayLabels {
  return parseEvaluationLevelDisplayLabels({
    level_label_a: labels.level_label_a.trim(),
    level_label_b: labels.level_label_b.trim(),
    level_label_c: labels.level_label_c.trim(),
    level_label_d: labels.level_label_d.trim(),
    level_label_e: labels.level_label_e.trim(),
  });
}

/** 워크스페이스 성취수준 표시 설정: 단계 수(2~5)별 A~E 대체 텍스트 */
export type EvaluationLevelDisplayLabelsByStepCount = Record<EvaluationLevelStepCount, EvaluationLevelDisplayLabels>;

export function emptyEvaluationLevelDisplayLabelsByStepCount(): EvaluationLevelDisplayLabelsByStepCount {
  return {
    2: { ...EMPTY_LEVEL_DISPLAY_LABELS },
    3: { ...EMPTY_LEVEL_DISPLAY_LABELS },
    4: { ...EMPTY_LEVEL_DISPLAY_LABELS },
    5: { ...EMPTY_LEVEL_DISPLAY_LABELS },
  };
}

/** 워크스페이스 성취수준 표시·기본 단계 설정 */
export type WorkspaceEvaluationPlanLevelSettings = {
  defaultLevelStepCount: EvaluationLevelStepCount;
  labelsByStep: EvaluationLevelDisplayLabelsByStepCount;
};

export function emptyWorkspaceEvaluationPlanLevelSettings(): WorkspaceEvaluationPlanLevelSettings {
  return {
    defaultLevelStepCount: DEFAULT_LEVEL_STEP_COUNT,
    labelsByStep: emptyEvaluationLevelDisplayLabelsByStepCount(),
  };
}

export function parseEvaluationLevelDisplayLabelsByStepCount(
  raw: unknown
): EvaluationLevelDisplayLabelsByStepCount {
  const base = emptyEvaluationLevelDisplayLabelsByStepCount();
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return base;
  const record = raw as Record<string, unknown>;
  for (const step of LEVEL_STEP_COUNT_OPTIONS) {
    const entry = record[String(step)];
    if (entry && typeof entry === 'object' && !Array.isArray(entry)) {
      base[step] = parseEvaluationLevelDisplayLabels(entry as Partial<EvaluationLevelDisplayLabels>);
    }
  }
  return base;
}

export function levelDisplayLabelsByStepCountToDbPayload(
  settings: EvaluationLevelDisplayLabelsByStepCount
): Record<string, EvaluationLevelDisplayLabels> {
  const payload: Record<string, EvaluationLevelDisplayLabels> = {};
  for (const step of LEVEL_STEP_COUNT_OPTIONS) {
    payload[String(step)] = levelDisplayLabelsToDbPayload(settings[step]);
  }
  return payload;
}

export function getLevelDisplayLabelsForStep(
  settings: EvaluationLevelDisplayLabelsByStepCount,
  stepCount: EvaluationLevelStepCount
): EvaluationLevelDisplayLabels {
  return parseEvaluationLevelDisplayLabels(settings[clampLevelStepCount(stepCount)]);
}

export type AdminEvaluationPlanCategory = {
  id: string;
  workspace_id: string | null;
  /** 워크스페이스 행만 사용. admin 전역(workspace_id null)은 null */
  grade_id?: string | null;
  category_name: string;
  level_step_count: EvaluationLevelStepCount;
  level_label_a: string;
  level_label_b: string;
  level_label_c: string;
  level_label_d: string;
  level_label_e: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
};

export type AdminEvaluationStandard = {
  id: string;
  workspace_id: string | null;
  grade_id?: string | null;
  category_name: string;
  track_text: string;
  area_text: string;
  achievement_code: string;
  achievement_text: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
} & EvaluationPlanLevelFields;

export type AdminEvaluationPlan = {
  id: string;
  workspace_id: string | null;
  grade_id?: string | null;
  category_name: string;
  standard_ids: string[];
  evaluation_element_text: string;
  evaluation_method_text: string;
  unit_text: string;
  lesson_evaluation_text: string;
  criterion_rubric_attachment: EvaluationPlanAttachment | null;
  evaluation_sheet_attachment: EvaluationPlanAttachment | null;
  /** 평가계획별 성취수준 단계(2~5) */
  level_step_count: EvaluationLevelStepCount;
  sort_order: number;
  created_at: string;
  updated_at: string;
} & EvaluationPlanLevelFields;

export type EvaluationStandardDraft = Omit<
  AdminEvaluationStandard,
  'workspace_id' | 'category_name' | 'created_at' | 'updated_at'
> & {
  category_name?: string;
};

export type EvaluationPlanDraft = Omit<
  AdminEvaluationPlan,
  'workspace_id' | 'created_at' | 'updated_at'
>;

export type EvaluationStandardUpsert = Omit<
  EvaluationStandardDraft,
  'id' | 'sort_order'
> & {
  id?: string;
  sort_order?: number;
};

export type EvaluationPlanUpsert = Omit<
  EvaluationPlanDraft,
  'id' | 'sort_order'
> & {
  id?: string;
  sort_order?: number;
};

export type EvaluationPlanCategoryBundle = {
  categoryName: string;
  standards: EvaluationStandardDraft[];
  plans: EvaluationPlanDraft[];
};

/** 기본 평가계획 등록: 행(평가계획) 출처 스코프 */
export type EvaluationPlanRowScope = 'admin' | 'team' | 'user';

export function categoryLevelMetaKey(scope: EvaluationPlanRowScope, categoryName: string): string {
  return `${scope}:${categoryName.trim()}`;
}

export type EvaluationPlanWorkspaceDraft = {
  categoryName: string;
  standards: EvaluationStandardDraft[];
  plans: EvaluationPlanDraft[];
  deletedStandardIds: string[];
  deletedPlanIds: string[];
};

export const EMPTY_EVALUATION_LEVELS: EvaluationPlanLevelFields = {
  level_a: '',
  level_b: '',
  level_c: '',
  level_d: '',
  level_e: '',
};

export function copyEvaluationLevels(source: EvaluationPlanLevelFields): EvaluationPlanLevelFields {
  return {
    level_a: source.level_a ?? '',
    level_b: source.level_b ?? '',
    level_c: source.level_c ?? '',
    level_d: source.level_d ?? '',
    level_e: source.level_e ?? '',
  };
}

/**
 * 성취기준에 적힌 성취수준(비어 있지 않은 값)을 왼쪽부터 모아
 * 평가계획 단계 수에 맞게 level_a… 에 채운다. 여러 기준은 줄바꿈으로 합친다.
 */
export function buildPlanLevelsFromStandards(
  standards: readonly EvaluationPlanLevelFields[],
  levelStepCount: EvaluationLevelStepCount
): EvaluationPlanLevelFields {
  const activeKeys = getActiveLevelKeys(levelStepCount);
  const out: EvaluationPlanLevelFields = { ...EMPTY_EVALUATION_LEVELS };
  if (!standards.length || activeKeys.length === 0) return out;

  const packedPerStandard = standards.map((row) =>
    EVALUATION_LEVEL_KEYS.map((key) => (row[key] ?? '').trim()).filter(Boolean)
  );

  activeKeys.forEach((key, idx) => {
    out[key] = packedPerStandard
      .map((packed) => packed[idx] ?? '')
      .filter(Boolean)
      .join('\n');
  });
  return out;
}

export function formatAchievementStandardLabel(standard: Pick<AdminEvaluationStandard, 'achievement_code' | 'achievement_text'>): string {
  const text = (standard.achievement_text ?? '').trim();
  if (text) return standard.achievement_text ?? '';
  return (standard.achievement_code ?? '').trim();
}

export function formatStandardSummary(standard: Pick<AdminEvaluationStandard, 'track_text' | 'area_text' | 'achievement_code' | 'achievement_text'>): string {
  const track = (standard.track_text ?? '').trim();
  const area = (standard.area_text ?? '').trim();
  const achievement = formatAchievementStandardLabel(standard).trim();
  return [track, area, achievement].filter(Boolean).join(' / ');
}

export function compareStandardsByCode(
  a: Pick<AdminEvaluationStandard, 'achievement_code' | 'achievement_text' | 'sort_order' | 'id' | 'track_text' | 'area_text'>,
  b: Pick<AdminEvaluationStandard, 'achievement_code' | 'achievement_text' | 'sort_order' | 'id' | 'track_text' | 'area_text'>
): number {
  const orderCmp = (a.sort_order ?? 0) - (b.sort_order ?? 0);
  if (orderCmp !== 0) return orderCmp;
  const trackCmp = (a.track_text ?? '').localeCompare(b.track_text ?? '', 'ko');
  if (trackCmp !== 0) return trackCmp;
  const areaCmp = (a.area_text ?? '').localeCompare(b.area_text ?? '', 'ko');
  if (areaCmp !== 0) return areaCmp;
  const textCmp = formatAchievementStandardLabel(a).localeCompare(formatAchievementStandardLabel(b), 'ko');
  if (textCmp !== 0) return textCmp;
  return (a.id ?? '').localeCompare(b.id ?? '');
}

/** 평가계획: 연결 성취기준(편제·영역·성취기준) 목록 순서 기준 정렬. */
export function comparePlansByLinkedStandards(
  a: Pick<EvaluationPlanDraft, 'id' | 'sort_order' | 'standard_ids'>,
  b: Pick<EvaluationPlanDraft, 'id' | 'sort_order' | 'standard_ids'>,
  standards: Pick<
    AdminEvaluationStandard,
    'id' | 'track_text' | 'area_text' | 'achievement_code' | 'achievement_text' | 'sort_order'
  >[]
): number {
  const byId = new Map(standards.map((row) => [row.id, row]));
  const linkedSorted = (plan: Pick<EvaluationPlanDraft, 'standard_ids'>) =>
    (plan.standard_ids ?? [])
      .map((id) => byId.get(id))
      .filter((row): row is NonNullable<typeof row> => !!row)
      .sort(compareStandardsByCode);

  const aLinked = linkedSorted(a);
  const bLinked = linkedSorted(b);
  if (aLinked.length === 0 && bLinked.length === 0) {
    return (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.id.localeCompare(b.id);
  }
  if (aLinked.length === 0) return 1;
  if (bLinked.length === 0) return -1;

  const len = Math.max(aLinked.length, bLinked.length);
  for (let i = 0; i < len; i += 1) {
    const left = aLinked[i];
    const right = bLinked[i];
    if (!left && right) return 1;
    if (left && !right) return -1;
    if (!left || !right) continue;
    const cmp = compareStandardsByCode(left, right);
    if (cmp !== 0) return cmp;
  }
  return (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.id.localeCompare(b.id);
}

export function standardMatchesFilter(
  standard: Pick<AdminEvaluationStandard, 'track_text' | 'area_text' | 'achievement_code' | 'achievement_text'>,
  query: string
): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    standard.track_text,
    standard.area_text,
    standard.achievement_code,
    standard.achievement_text,
    formatAchievementStandardLabel(standard),
    formatStandardSummary(standard),
  ]
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
}

export function formatPlanSummary(
  plan: Pick<EvaluationPlanDraft, 'evaluation_element_text' | 'evaluation_method_text' | 'unit_text'>
): string {
  const unit = (plan.unit_text ?? '').trim();
  const element = (plan.evaluation_element_text ?? '').trim();
  const method = (plan.evaluation_method_text ?? '').trim();
  if (!unit && !element && !method) return '(내용 없음)';
  return `${unit} / ${element} / ${method}`;
}

/** 평가 방법 이름(짧게): '[서술형][관찰] …' → ['서술형','관찰'] — 태그가 없으면 알려진 방법 낱말로 */
const KNOWN_METHOD_NAMES = [
  '서술형', '논술형', '구술', '발표', '토의·토론', '토의', '토론', '프로젝트', '실기', '실험·실습', '보고서법', '보고서',
  '포트폴리오', '관찰', '자기 평가', '동료 평가', '정의적 능력 평가', '협력적 문제해결력 평가', '면접',
];
export function extractPlanMethodNames(methodText: string): string[] {
  const text = String(methodText ?? '');
  const out: string[] = [];
  const add = (v: string) => {
    const t = v.replace(/\s+/g, ' ').trim();
    if (t && !out.includes(t)) out.push(t);
  };
  const re = /\[([^\[\]]{1,20})\]/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text))) add(m[1]);
  if (out.length) return out;
  const squashed = text.replace(/\s+/g, '');
  KNOWN_METHOD_NAMES.forEach((name) => {
    const key = name.replace(/\s+/g, '');
    if (!squashed.includes(key)) return;
    // '토의·토론'이 이미 있으면 '토의'/'토론' 따로 넣지 않음
    if (out.some((o) => o.replace(/\s+/g, '').includes(key))) return;
    add(name);
  });
  return out;
}

/** 목록 표시용: '단원 / 평가요소 / 평가방법(짧은 이름)' — 저장값은 바꾸지 않음 */
export function formatPlanListSummary(
  plan: Pick<EvaluationPlanDraft, 'evaluation_element_text' | 'evaluation_method_text' | 'unit_text'>
): string {
  const unit = (plan.unit_text ?? '').trim();
  const element = String(plan.evaluation_element_text ?? '')
    .split('\n')
    .map((l) => l.replace(/^[\s▪·•\-]+/, '').trim())
    .filter(Boolean)
    .join(', ');
  const rawMethod = (plan.evaluation_method_text ?? '').trim();
  const names = extractPlanMethodNames(rawMethod);
  const method = names.length ? names.join(', ') : rawMethod.replace(/^[\s▪·•\-]+/, '');
  if (!unit && !element && !method) return '(내용 없음)';
  return `${unit} / ${element} / ${method}`;
}

export function planMatchesFilter(
  plan: Pick<
    EvaluationPlanDraft,
    | 'evaluation_element_text'
    | 'evaluation_method_text'
    | 'unit_text'
    | 'lesson_evaluation_text'
    | 'level_a'
    | 'level_b'
    | 'level_c'
    | 'level_d'
    | 'level_e'
    | 'standard_ids'
  >,
  query: string,
  standards?: Pick<
    AdminEvaluationStandard,
    'id' | 'track_text' | 'area_text' | 'achievement_code' | 'achievement_text' | 'sort_order'
  >[]
): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;

  const linkedStandardParts: string[] = [];
  if (standards?.length) {
    const byId = new Map(standards.map((row) => [row.id, row]));
    for (const id of plan.standard_ids ?? []) {
      const standard = byId.get(id);
      if (!standard) continue;
      linkedStandardParts.push(
        standard.track_text,
        standard.area_text,
        standard.achievement_code,
        standard.achievement_text,
        formatAchievementStandardLabel(standard),
        formatStandardSummary(standard)
      );
    }
  }

  const haystack = [
    plan.evaluation_element_text,
    plan.evaluation_method_text,
    plan.unit_text,
    plan.lesson_evaluation_text,
    plan.level_a,
    plan.level_b,
    plan.level_c,
    plan.level_d,
    plan.level_e,
    formatPlanSummary(plan),
    ...linkedStandardParts,
  ]
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
}

export function planLinksStandard(
  plan: Pick<EvaluationPlanDraft, 'standard_ids'>,
  standardId: string
): boolean {
  return (plan.standard_ids ?? []).includes(standardId);
}

export function getPlanLinkedStandardLines(
  plan: Pick<EvaluationPlanDraft, 'standard_ids'>,
  standards: Pick<
    AdminEvaluationStandard,
    'id' | 'track_text' | 'area_text' | 'achievement_code' | 'achievement_text' | 'sort_order'
  >[]
): string[] {
  const byId = new Map(standards.map((row) => [row.id, row]));
  return (plan.standard_ids ?? [])
    .map((id) => byId.get(id))
    .filter((row): row is NonNullable<typeof row> => !!row)
    .sort(compareStandardsByCode)
    .map((row) => formatStandardSummary(row));
}

export function formatPlanLinkedStandardsSummary(
  plan: Pick<EvaluationPlanDraft, 'standard_ids'>,
  standards: Pick<AdminEvaluationStandard, 'id' | 'track_text' | 'area_text' | 'achievement_code' | 'achievement_text' | 'sort_order'>[]
): string {
  const lines = getPlanLinkedStandardLines(plan, standards);
  return lines.join(' · ') || '(연결 성취기준 없음)';
}

export function createEmptyStandardDraft(categoryName: string, sortOrder: number): EvaluationStandardDraft {
  return {
    id: crypto.randomUUID(),
    category_name: categoryName,
    track_text: '',
    area_text: '',
    achievement_code: '',
    achievement_text: '',
    sort_order: sortOrder,
    ...EMPTY_EVALUATION_LEVELS,
  };
}

export function createEmptyPlanDraft(
  categoryName: string,
  sortOrder: number,
  standardIds: string[] = [],
  levelStepCount: EvaluationLevelStepCount = DEFAULT_LEVEL_STEP_COUNT
): EvaluationPlanDraft {
  return {
    id: crypto.randomUUID(),
    grade_id: null,
    category_name: categoryName,
    standard_ids: [...standardIds],
    evaluation_element_text: '',
    evaluation_method_text: '',
    unit_text: '',
    lesson_evaluation_text: '',
    criterion_rubric_attachment: null,
    evaluation_sheet_attachment: null,
    level_step_count: clampLevelStepCount(levelStepCount),
    sort_order: sortOrder,
    ...EMPTY_EVALUATION_LEVELS,
  };
}

export function normalizeEvaluationPlanWorkspaceDraft(draft: EvaluationPlanWorkspaceDraft): string {
  const standards = [...draft.standards].sort(compareStandardsByCode);
  const plans = [...draft.plans]
    .map((row) => ({
      ...row,
      category_name: row.category_name ?? draft.categoryName,
      standard_ids: [...(row.standard_ids ?? [])].sort(),
    }))
    .sort((a, b) => a.sort_order - b.sort_order || a.id.localeCompare(b.id));
  const deletedStandardIds = [...draft.deletedStandardIds].sort();
  const deletedPlanIds = [...draft.deletedPlanIds].sort();
  return JSON.stringify({ standards, plans, deletedStandardIds, deletedPlanIds, categoryName: draft.categoryName });
}
